import { SubagentAccess } from 'js/src/agent/subagents/subagentAccess';
import { TOOL_NAMES } from 'js/src/agent/toolNames';
import styles from 'css/src/components/agent/AskUserQuestionResult.module.scss';

import React from 'react';
import { z } from 'zod';

import { markQuestionAsked } from 'js/src/data/agent-activity';

import { ask } from '../ask-user-question/askUserQuestionStore';
import { introForAsker } from '../subagents/subagentQuestions';
import { resolveClipQuestion } from '../ask-user-question/clip-question-validation';
import type { AskQuestion, AskQuestionAnswer, LocatedClip } from '../ask-user-question/types';
import { askQuestionSchema } from '../ask-user-question/types';
import {
    type CustomRendererComponentProps,
    CustomRendererMode,
    type EnhancedToolResult,
    type ToolExecutionContext,
} from '../types';
import { Tool } from './Tool';

const askUserQuestionSchema = z.object({
    intro: z
        .string()
        .optional()
        .describe('Optional note shown above the questions. Plain text, no markdown.'),
    questions: z
        .array(askQuestionSchema)
        .min(1)
        // Said once here rather than in every branch of the union, which each
        // repeat the fields they share.
        .describe(
            'The questions to ask the user. Each one is a shape below, picked by its "type". ' +
                'Every "question" and optional "description" (shown under the question) is plain text, no markdown.',
        ),
});

type AskUserQuestionArgs = z.infer<typeof askUserQuestionSchema>;

const ANSWER_PREFIX = 'The user answered:';
/**
 * Exported because the renderer recognises a dismissal by comparing against this
 * exact string — a test that keeps its own copy of the wording silently stops
 * exercising the dismissed path the moment the copy here is reworded.
 */
export const DISMISSED_RESULT =
    "The user dismissed the questions without answering. Don't ask these questions again, instead proceed using your best judgement.";

function formatAnswer(answer: AskQuestionAnswer): string {
    const parts: string[] = [];
    // App-driven kinds resolve to a human-readable name (voice, template, brand,
    // uploaded file), but the agent has to act on the pick — so report the id
    // when the widget has one. A cleared pick carries an empty name, which is a
    // skip rather than an answer.
    if (answer.resolved?.name) {
        parts.push(answer.resolved.id ?? answer.resolved.name);
    }
    if (answer.selectedClips) {
        parts.push(
            answer.selectedClips.length > 0
                ? answer.selectedClips
                      .map(
                          (clip) =>
                              `[${clip.index}] ${clip.label}: start_quote "${clip.start_quote}", end_quote "${clip.end_quote}"`,
                      )
                      .join(', ')
                : 'none of the clips',
        );
    } else if (answer.selectedLabels.length > 0) {
        parts.push(answer.selectedLabels.join(', '));
    }
    if (answer.customText) {
        parts.push(`custom answer: "${answer.customText}"`);
    }
    const value = parts.length > 0 ? parts.join('; ') : '(no answer)';
    return `- "${answer.question}": ${value}`;
}

const REFERENCE_PREFIX = 'Uploaded assets, ready to use:';

/**
 * Asset references for any uploads, gathered into a block after the answer list.
 * They're kept off the `- "{question}": …` lines so those stay single-line and
 * parseable for display, while the agent still gets the IDs it needs to place
 * the assets in the video.
 */
function formatReferences(answers: AskQuestionAnswer[]): string {
    const references = answers.flatMap((answer) => answer.resolved?.references ?? []);
    return references.length > 0 ? `\n\n${REFERENCE_PREFIX}\n${references.join('\n')}` : '';
}

interface ParsedAnswer {
    question: string;
    value: string;
}

/** Pulls the `- "{question}": {value}` lines back out for display. */
function parseAnswers(result: string): ParsedAnswer[] {
    if (!result.startsWith(ANSWER_PREFIX)) {
        return [];
    }
    return result
        .split('\n')
        .filter((line) => line.startsWith('- '))
        .map((line) => {
            const match = line.match(/^- "(.*?)": (.*)$/);
            if (!match) {
                return null;
            }
            return { question: match[1], value: match[2] };
        })
        .filter((answer): answer is ParsedAnswer => answer !== null);
}

export const AskUserQuestionResultRenderer: React.FC<CustomRendererComponentProps> = ({
    isLoading,
    result,
}) => {
    if (isLoading) {
        return null;
    }
    if (result === DISMISSED_RESULT) {
        return <div className={styles['dismissed']}>Dismissed without answering</div>;
    }
    const answers = parseAnswers(result);
    if (answers.length === 0) {
        return null;
    }
    return (
        <div className={styles['answers']}>
            {answers.map((answer, index) => (
                <div key={index} className={styles['answer']}>
                    <span className={styles['answer-header']}>{answer.question}</span>
                    <span className={styles['answer-value']}>{answer.value}</span>
                </div>
            ))}
        </div>
    );
};

export class AskUserQuestionTool extends Tool<typeof askUserQuestionSchema> {
    public override customRenderer = AskUserQuestionResultRenderer;
    public override customRendererMode = CustomRendererMode.INLINE_HIDDEN;

    protected override readonly schema = askUserQuestionSchema;
    protected override readonly toolName = TOOL_NAMES.askUserQuestion;
    public override readonly subagentAccess = SubagentAccess.ASKS_USER;
    protected override readonly toolDescription =
        'Ask the user one or more questions and wait for their answer. Use this for a decision or preference you cannot reasonably infer, instead of guessing. Bundle every question into a single call rather than asking piecemeal. The user can always dismiss without answering. Do not use markdown.';

    public override getToolDisplayName(toolCallArgs?: string): string {
        if (toolCallArgs) {
            try {
                const parsed = JSON.parse(toolCallArgs) as Partial<AskUserQuestionArgs>;
                if ((parsed.questions?.length ?? 0) > 1) {
                    return `Asking ${parsed.questions!.length} questions`;
                }
            } catch {
                // fall through to the singular name
            }
        }
        return 'Asking a question';
    }

    public get statusIcon(): string {
        return 'quiz';
    }

    public async execute(
        args: AskUserQuestionArgs,
        context: ToolExecutionContext,
    ): Promise<EnhancedToolResult> {
        const abortSignal = context.abortSignal;
        if (abortSignal?.aborted) {
            return { result: 'Question cancelled before it was shown.', isSuccess: false };
        }

        const questions: AskQuestion[] = [];
        for (const question of args.questions) {
            if (question.type !== 'clip_select') {
                questions.push(question);
                continue;
            }
            const resolution = await resolveClipQuestion(question);
            if (!resolution.ok) {
                return { result: `Error: ${resolution.error}`, isSuccess: false };
            }
            const clips: LocatedClip[] = question.clips.map((clip, index) => ({
                ...clip,
                ...resolution.spans[index],
            }));
            questions.push({ ...question, clips });
        }

        this.dispatch(markQuestionAsked());
        const result = await ask(
            questions,
            context.conversationId,
            introForAsker(context.subagentDescription, args.intro),
            abortSignal,
        );

        if (result.status === 'dismissed') {
            return { result: DISMISSED_RESULT, isSuccess: true };
        }

        const formatted = result.answers.map(formatAnswer).join('\n');
        return {
            result: `${ANSWER_PREFIX}\n${formatted}${formatReferences(result.answers)}`,
            isSuccess: true,
        };
    }
}
